import React, { useState, useMemo, useContext } from "react";
import { Search, X, Check } from "lucide-react";
import moazamiMacros from "../../../libs/macros/MoazamiDotPhrases.json";
import VADemoMacros from "../../../libs/macros/VADemoDotPhrases.json";
import { MacrosContext } from "../../../utils/MacrosContext";
import userJSON from "../../../utils/userJSON";
import sendServerLogging from "../../../utils/sendServerLogging";
import { mergeTelehealthAvailableMacros } from "../../../utils/telehealthMacros";
import { useFeatureGate } from "../../../hooks/useFeatureGate";

export default function MacrosFeature({
  macros,
  setMacros,
  setNote,
  cursorPosition,
  setCursorPosition,
  textareaRef,
  summaryTextareaRef,
  setPatientSummary,
  name,
  status,
  patient,
}) {
  const suggestedDotPhrases = macros?.suggested_macros || [];

  // Initialize macros if null (for users like Dr.VADemo who don't have backend macro data)
  React.useEffect(() => {
    if (macros === null) {
      setMacros({
        suggested_macros: [],
        added_macros: [],
      });
    }
  }, [macros, setMacros]);

  const [searchTerm, setSearchTerm] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const { userMacros, fetchUserMacros } = useContext(MacrosContext);
  const vvcDotphraseGate = useFeatureGate("vvc-dotphrase");
  const hasVvcDotphraseAccess = vvcDotphraseGate.hasFeature("on");
  const hasFetchedUserMacros = React.useRef(false);
  const listboxId = `macros-search-listbox-${patient?.patient_id || "default"}`;
  const suggestedIdPrefix = `macros-suggested-${patient?.patient_id || "default"}`;

  //state var to handle macro hover events
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const [expandedTooltip, setExpandedTooltip] = useState(null);

  React.useEffect(() => {
    if (hasFetchedUserMacros.current) {
      return;
    }
    if (typeof fetchUserMacros === "function") {
      hasFetchedUserMacros.current = true;
      fetchUserMacros();
    }
  }, [fetchUserMacros]);

  const doctorMacroLibrary = useMemo(() => {
    if (Array.isArray(userMacros)) {
      if (userMacros.length === 0) {
        return mergeTelehealthAvailableMacros([], hasVvcDotphraseAccess);
      }

      return mergeTelehealthAvailableMacros(userMacros, hasVvcDotphraseAccess)
        .map((macro) => ({
          dot_phrase: macro?.dot_phrase || "",
          value: macro?.content || "",
          keywords: Array.isArray(macro?.keywords)
            ? macro.keywords.join(", ")
            : macro?.keywords || "",
        }))
        .filter((macro) => macro.dot_phrase);
    }
    if (name === "Dr. Moazami") {
      return mergeTelehealthAvailableMacros(moazamiMacros, hasVvcDotphraseAccess);
    }
    if (name === "Dr. VA Demo") {
      return mergeTelehealthAvailableMacros(VADemoMacros, hasVvcDotphraseAccess);
    }
    return mergeTelehealthAvailableMacros([], hasVvcDotphraseAccess);
  }, [name, userMacros, hasVvcDotphraseAccess]);

  const macrosByDotPhrase = useMemo(() => {
    const map = new Map();

    const addList = (list) => {
      if (!Array.isArray(list)) return;
      list.forEach((item) => {
        if (!item?.dot_phrase) return;
        map.set(item.dot_phrase.toLowerCase(), item);
      });
    };

    addList(doctorMacroLibrary);
    addList(macros?.added_macros);
    addList(macros?.accepted);
    addList(suggestedDotPhrases);

    return map;
  }, [doctorMacroLibrary, macros, suggestedDotPhrases]);

  // Filter dot phrases based on search term
  const filteredDotPhrases = useMemo(() => {
    if (!searchTerm.trim()) return [];

    const lowerSearchTerm = searchTerm.toLowerCase();

    return doctorMacroLibrary.filter(
      (item) =>
        item.keywords?.toLowerCase().includes(lowerSearchTerm) ||
        item.dot_phrase.toLowerCase().includes(lowerSearchTerm)
    );
  }, [searchTerm, doctorMacroLibrary]);

  // Check if a dot phrase is already selected (in either accepted or added_macros)
  const isSelected = (dotPhrase) => {
    return (
      macros?.accepted?.some((item) => item.dot_phrase === dotPhrase) ||
      macros?.added_macros?.some((item) => item.dot_phrase === dotPhrase)
    );
  };

  // Check if in accepted array
  const isAccepted = (dotPhrase) => {
    return macros?.accepted?.some((item) => item.dot_phrase === dotPhrase);
  };

  // Check if in added_macros array
  const isAddedMacro = (dotPhrase) => {
    return macros?.added_macros?.some((item) => item.dot_phrase === dotPhrase);
  };

  // Handle selection from search bar - goes to added_macros
  const handleSelectDotPhrase = (selectedItem) => {
    if (!isSelected(selectedItem.dot_phrase)) {
      setMacros((prev) => ({
        ...prev,
        added_macros: [...(prev.added_macros || []), selectedItem],
      }));

      // Add the macro value to the note
      setNote((prevNote) => {
        const noteText = prevNote ?? "";
        let updatedNote = noteText;

        if (
          cursorPosition !== null &&
          cursorPosition !== undefined &&
          cursorPosition >= 0
        ) {
          const textBefore = noteText.substring(0, cursorPosition);
          const textAfter = noteText.substring(cursorPosition);
          const needsNewlineBefore =
            textBefore.length > 0 && !textBefore.endsWith("\n");
          const prefix = needsNewlineBefore ? "\n" : "";
          const insertionText = prefix + selectedItem.value + "\n";
          updatedNote = textBefore + insertionText + textAfter;

          const newCursorPosition = cursorPosition + insertionText.length;
          setCursorPosition(newCursorPosition);

          if (textareaRef?.current) {
            setTimeout(() => {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(
                newCursorPosition,
                newCursorPosition
              );
            }, 0);
          }
        } else {
          const insertionText = `${selectedItem.value}\n`;
          updatedNote = insertionText + noteText;
          setCursorPosition(insertionText.length);
          if (textareaRef?.current) {
            setTimeout(() => {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(
                insertionText.length,
                insertionText.length
              );
            }, 0);
          }
        }
        //server logging
        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "SEARCHED_MACRO_ADDED",
            eventData: {
              mrn: patient.mrn,
              macro: selectedItem.dot_phrase,
              patientId: patient.patient_id,
            },
          },
        });

        return updatedNote;
      });
    }
    setSearchTerm("");
    setShowResults(false);
  };

  // Generic remove function that checks both arrays
  const handleRemoveDotPhrase = (dotPhrase) => {
    // Check if it's in accepted array
    const acceptedItem = macros.accepted?.find(
      (item) => item.dot_phrase === dotPhrase
    );

    // Check if it's in added_macros array
    const addedItem = macros.added_macros?.find(
      (item) => item.dot_phrase === dotPhrase
    );

    const itemToRemove = acceptedItem || addedItem;

    if (acceptedItem) {
      setMacros((prev) => ({
        ...prev,
        accepted: prev.accepted?.filter(
          (item) => item.dot_phrase !== dotPhrase
        ),
      }));
    } else if (addedItem) {
      setMacros((prev) => ({
        ...prev,
        added_macros: prev.added_macros?.filter(
          (item) => item.dot_phrase !== dotPhrase
        ),
      }));
    }

    // Remove from note if item found
    if (itemToRemove) {
      setNote((prevNote) => {
        if (!prevNote) return prevNote;

        const escapedValue = itemToRemove.value.replace(
          /[.*+?^${}()|[\]\\]/g,
          "\\$&"
        );

        let updatedNote = prevNote;
        const removalRegex = new RegExp(
          `(\\n?)(?:-\\s*)?${escapedValue}\\n`,
          "g"
        );

        updatedNote = updatedNote.replace(
          removalRegex,
          (_, leadingNewline) => leadingNewline || ""
        );

        updatedNote = updatedNote.replace(/\n{3,}/g, "\n\n");
        updatedNote = updatedNote.replace(/^\n{3,}/, "\n\n");

        if (!updatedNote.trim()) {
          return "";
        }

        return updatedNote;
      });
    }
  };

  // Handle checkbox selection for suggested dot phrases - goes to accepted
  const handleSuggestedSelect = (item) => {
    if (isAccepted(item.dot_phrase)) {
      handleRemoveDotPhrase(item.dot_phrase);
    } else if (!isSelected(item.dot_phrase)) {
      setMacros((prev) => ({
        ...prev,
        accepted: [...(prev.accepted || []), item],
      }));

      // Add the macro value to the note at cursor position
      setNote((prevNote) => {
        const noteText = prevNote ?? "";
        let updatedNote = noteText;
        sendServerLogging({
          body: {
            ...userJSON(localStorage.getItem("userName")),
            event: "SUGGESTED_MACRO_ADDED",
            eventData: { mrn: patient.mrn, macro: item.dot_phrase },
            patientId: patient.patient_id,
          },
        });

        // Check if we have a valid cursor position
        if (
          cursorPosition !== null &&
          cursorPosition !== undefined &&
          cursorPosition >= 0
        ) {
          // Insert at cursor position without adding list bullets
          const textBefore = noteText.substring(0, cursorPosition);
          const textAfter = noteText.substring(cursorPosition);
          // Ensure the macro starts on a new line if needed
          const needsLineBefore =
            textBefore.length > 0 && !textBefore.endsWith("\n");
          const prefix = needsLineBefore ? "\n" : "";
          const insertionText = prefix + item.value + "\n";
          updatedNote = textBefore + insertionText + textAfter;

          // Update cursor position to after the inserted text
          const newCursorPosition = cursorPosition + insertionText.length;
          setCursorPosition(newCursorPosition);

          if (textareaRef?.current) {
            setTimeout(() => {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(
                newCursorPosition,
                newCursorPosition
              );
            }, 0);
          }
        } else {
          const insertionText = `${item.value}\n`;
          updatedNote = insertionText + noteText;
          setCursorPosition(insertionText.length);
          if (textareaRef?.current) {
            setTimeout(() => {
              textareaRef.current.focus();
              textareaRef.current.setSelectionRange(
                insertionText.length,
                insertionText.length
              );
            }, 0);
          }
        }
        return updatedNote;
      });
    }
  };

  // Get all selected macros from both arrays
  const getAllSelectedMacros = () => {
    const accepted = macros?.accepted || [];
    const added = macros?.added_macros || [];
    return [...accepted, ...added];
  };

  React.useEffect(() => {
    const textareaEl = textareaRef?.current;
    const summaryTextareaEl = summaryTextareaRef?.current;

    if (!textareaEl && !summaryTextareaEl) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Enter" || event.shiftKey) return;
      if (!(event.target instanceof HTMLTextAreaElement)) return;

      const textarea = event.target;
      if (status === "Locked") return;

      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;

      if (selectionStart === null || selectionEnd === null) return;
      if (selectionStart !== selectionEnd) return;

      const textBefore = textarea.value.slice(0, selectionStart);
      const dotPhraseMatch = textBefore.match(/(\.[A-Za-z0-9]+)\s*$/);

      if (!dotPhraseMatch) return;

      const rawMatch = dotPhraseMatch[0];
      const dotPhrase = dotPhraseMatch[1];
      const insertionStart = selectionStart - rawMatch.length;

      const charBefore =
        insertionStart > 0 ? textBefore[insertionStart - 1] : "";
      if (charBefore && /[A-Za-z0-9]/.test(charBefore)) {
        return;
      }

      const macro = macrosByDotPhrase.get(dotPhrase.toLowerCase()) || undefined;

      if (!macro) return;

      event.preventDefault();

      const suggestedMatch =
        suggestedDotPhrases.find(
          (item) => item?.dot_phrase?.toLowerCase() === dotPhrase.toLowerCase()
        ) || null;
      const isSummaryEditor = textarea === summaryTextareaEl;
      const setEditorText = isSummaryEditor ? setPatientSummary : setNote;

      if (typeof setEditorText !== "function") return;

      let nextCursorPosition = insertionStart;

      setEditorText((previousText) => {
        const currentText = previousText ?? "";
        const beforeMacro = currentText.slice(0, insertionStart);
        const afterMacro = currentText.slice(selectionEnd);
        const needsNewline =
          beforeMacro.length > 0 && !beforeMacro.endsWith("\n");
        const prefix = needsNewline ? "\n" : "";
        const insertionText = prefix + macro.value + "\n";
        nextCursorPosition = insertionStart + insertionText.length;

        return beforeMacro + insertionText + afterMacro;
      });

      if (!isSummaryEditor) {
        setCursorPosition(nextCursorPosition);
      }

      setTimeout(() => {
        const activeTextarea = isSummaryEditor ? summaryTextareaRef?.current : textareaRef?.current;
        if (activeTextarea) {
          activeTextarea.focus();
          activeTextarea.setSelectionRange(
            nextCursorPosition,
            nextCursorPosition
          );
        }
      }, 0);

      setMacros((prev) => {
        const base = prev || {
          suggested_macros: [],
          accepted: [],
          added_macros: [],
        };

        const alreadyAccepted = base.accepted?.some(
          (item) => item?.dot_phrase?.toLowerCase() === dotPhrase.toLowerCase()
        );
        const alreadyAdded = base.added_macros?.some(
          (item) => item?.dot_phrase?.toLowerCase() === dotPhrase.toLowerCase()
        );

        if (alreadyAccepted || alreadyAdded) {
          return base;
        }

        if (suggestedMatch) {
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "SUGGESTED_MACRO_ADDED",
              eventData: { mrn: patient.mrn, macro: suggestedMatch.dot_phrase },
              patientId: patient.patient_id,
            },
          });

          return {
            ...base,
            accepted: [...(base.accepted || []), suggestedMatch],
          };
        }

        return {
          ...base,
          added_macros: [...(base.added_macros || []), macro],
        };
      });
    };

    textareaEl?.addEventListener("keydown", handleKeyDown);
    summaryTextareaEl?.addEventListener("keydown", handleKeyDown);

    return () => {
      textareaEl?.removeEventListener("keydown", handleKeyDown);
      summaryTextareaEl?.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    textareaRef,
    summaryTextareaRef,
    macrosByDotPhrase,
    setNote,
    setPatientSummary,
    setCursorPosition,
    setMacros,
    suggestedDotPhrases,
    status,
    patient,
  ]);

  return (
    <div className="w-full mb-4">
      <div className="space-y-6">
        {/* Selected Dot Phrases*/}
        <div>
          <h2 className="text-md italic font-semibold mb-3 text-accent1">
            Selected Dot Phrases
          </h2>
          {getAllSelectedMacros().length === 0 ? (
            <p className="text-transparent-grey text-sm">
              No dot phrases selected.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {getAllSelectedMacros().map((item, index) => (
                <div
                  key={`${item.dot_phrase}-${index}`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-transparent-green rounded-lg border border-gray-200"
                >
                  <span className="font-mono font-semibold text-ktx-green">
                    {item.dot_phrase}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveDotPhrase(item.dot_phrase)}
                    className="p-0.5 text-gray-400 hover:text-red-600 transition-colors"
                    aria-label={`Remove ${item.dot_phrase}`}
                    disabled={status === "Locked"}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Side by Side Layout for Suggested Macros and Search */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Suggested Macros Section */}
          <div>
            <h2 className="text-md italic font-semibold mb-2 text-accent1">
              Knowtex's Suggested Dot Phrases
            </h2>

            {suggestedDotPhrases.length === 0 ? (
              <p className="text-transparent-grey text-sm">
                No suggested dot phrases available.
              </p>
            ) : (
              <div className="space-y-3">
                {suggestedDotPhrases.map((item, index) => (
                  <div
                    key={index}
                    className="relative flex items-start p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => {
                      setHoveredIndex(null);
                      setExpandedTooltip(null);
                    }}
                    onFocus={() => setHoveredIndex(index)}
                    onBlur={(event) => {
                      if (event.currentTarget.contains(event.relatedTarget)) return;
                      setHoveredIndex(null);
                      setExpandedTooltip(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape" && hoveredIndex === index) {
                        event.preventDefault();
                        event.stopPropagation();
                        event.nativeEvent?.stopImmediatePropagation?.();
                        // "Show more" lives in the preview and unmounts with it.
                        event.currentTarget.querySelector('input[type="checkbox"]')?.focus();
                        setHoveredIndex(null);
                        setExpandedTooltip(null);
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      id={`${suggestedIdPrefix}-${index}`}
                      aria-describedby={
                        hoveredIndex === index ? `${suggestedIdPrefix}-${index}-preview` : undefined
                      }
                      checked={isAccepted(item.dot_phrase)}
                      onChange={() => {
                        if (status !== "Locked") handleSuggestedSelect(item);
                      }}
                      className={`mt-1 mr-3 h-4 w-4 text-ktx-green rounded border-gray-300 focus:ring-ktx-green ${
                        status === "Locked" ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                      }`}
                      // aria-disabled keeps it focusable so keyboard users can still
                      // open the preview and Escape has somewhere to return focus.
                      aria-disabled={status === "Locked" ? "true" : undefined}
                    />
                    <label
                      htmlFor={`${suggestedIdPrefix}-${index}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="font-mono font-semibold text-accent1 text-sm">
                        {item.dot_phrase}
                      </div>
                    </label>
                    {/* Tooltip */}
                    {hoveredIndex === index && (
                      <div
                        id={`${suggestedIdPrefix}-${index}-preview`}
                        role="tooltip"
                        className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-64 p-3 bg-gray-800 text-white text-sm rounded shadow-lg z-10"
                      >
                        {/* Arrow pointing up */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 -mb-1">
                          <div className="border-4 border-transparent border-b-gray-800"></div>
                        </div>

                        {/* Expandable content */}
                        {item.value.length > 100 ? (
                          <div>
                            <p>
                              {expandedTooltip === index
                                ? item.value
                                : `${item.value.substring(0, 100)}...`}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedTooltip(
                                  expandedTooltip === index ? null : index
                                );
                              }}
                              className="text-blue-300 hover:text-blue-200 text-xs mt-2 underline"
                            >
                              {expandedTooltip === index
                                ? "Show less"
                                : "Show more"}
                            </button>
                          </div>
                        ) : (
                          <p>{item.value}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Search Section */}
          <div>
            <h3 className="text-md italic font-semibold mb-2 text-accent1">
              Search All Dot Phrases
            </h3>

            {/* Search Input */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                role="combobox"
                aria-label="Search dot phrases"
                aria-expanded={showResults && filteredDotPhrases.length > 0}
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-activedescendant={
                  activeResultIndex >= 0
                    ? `${listboxId}-option-${activeResultIndex}`
                    : undefined
                }
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowResults(e.target.value.length > 0);
                  setActiveResultIndex(-1);
                }}
                onFocus={() => setShowResults(searchTerm.length > 0)}
                onBlur={() => {
                  setTimeout(() => {
                    setShowResults(false);
                    setActiveResultIndex(-1);
                  }, 200);
                }}
                onKeyDown={(e) => {
                  if (!showResults || filteredDotPhrases.length === 0) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActiveResultIndex((prev) =>
                      prev < filteredDotPhrases.length - 1 ? prev + 1 : 0
                    );
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActiveResultIndex((prev) =>
                      prev > 0 ? prev - 1 : filteredDotPhrases.length - 1
                    );
                  } else if (e.key === "Enter" && activeResultIndex >= 0) {
                    e.preventDefault();
                    const item = filteredDotPhrases[activeResultIndex];
                    if (status !== "Locked") handleSelectDotPhrase(item);
                  } else if (e.key === "Escape") {
                    setShowResults(false);
                    setActiveResultIndex(-1);
                  }
                }}
                placeholder="Search for dot phrase..."
                className="block w-full pl-10 pr-3 py-2 italic text-light-grey border border-gray-300 rounded-lg focus:ring-2 focus:ring-ktx-green focus:border-ktx-green outline-none transition-colors"
                disabled={status == "Locked"}
                onClick={() => {
                  sendServerLogging({
                    body: {
                      ...userJSON(localStorage.getItem("userName")),
                      event: "SEARCH_MACROS",
                      eventData: { Search: searchTerm },
                      patientId: patient.patient_id,
                    },
                  });
                }}
              />
            </div>
            <div
              aria-live="polite"
              aria-atomic="true"
              className="sr-only"
            >
              {showResults && searchTerm
                ? filteredDotPhrases.length === 0
                  ? "No dot phrases found"
                  : `${filteredDotPhrases.length} dot phrase${filteredDotPhrases.length === 1 ? "" : "s"} found`
                : ""}
            </div>

            {/* Search Results */}
            {showResults && filteredDotPhrases.length > 0 && (
              <div
                id={listboxId}
                role="listbox"
                aria-label="Dot phrase search results"
                className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto"
              >
                {filteredDotPhrases.map((item, index) => (
                  <div
                    key={index}
                    id={`${listboxId}-option-${index}`}
                    role="option"
                    aria-selected={isSelected(item.dot_phrase)}
                    onClick={() =>
                      status !== "Locked" && handleSelectDotPhrase(item)
                    }
                    className={`px-4 py-3 hover:bg-gray-50 transition-colors ${
                      isSelected(item.dot_phrase) ? "bg-gray-50" : ""
                    } ${
                      activeResultIndex === index
                        ? "ring-2 ring-inset ring-ktx-green bg-gray-50"
                        : ""
                    } ${
                      status === "Locked"
                        ? "cursor-not-allowed opacity-60"
                        : "cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold text-ktx-green">
                            {item.dot_phrase}
                          </span>
                          {isSelected(item.dot_phrase) && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              Added
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {item.value}
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Keywords: {item.keywords || item.dot_phrase}
                        </div>
                      </div>
                      {isSelected(item.dot_phrase) && (
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 ml-2" aria-hidden="true" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No Results Message */}
            {showResults && searchTerm && filteredDotPhrases.length === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                <p className="text-gray-500 text-center">
                  No dot phrases found for "{searchTerm}"
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
